import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "../../firebase/config";
import { collection, getDocs } from "firebase/firestore";
import {
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area
} from "recharts";
import { format, subMonths } from "date-fns";

const COLORS = ["#16a34a", "#f59e0b", "#ef4444", "#3b82f6", "#8b5cf6", "#ec4899"];

const AdminAnalytics = () => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        console.log("Fetching subscriptions for analytics...");
        const snapshot = await getDocs(collection(db, "subscriptions"));
        const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setSubscriptions(data);
        console.log(`Loaded ${data.length} subscriptions`);
      } catch (error) {
        console.error("Error fetching subscriptions:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const getMonthlyRevenueData = () => {
    const months = [];
    const currentDate = new Date();

    for (let i = 5; i >= 0; i--) {
      const date = subMonths(currentDate, i);
      const monthKey = format(date, "MMM yyyy");
      months.push({
        month: monthKey,
        revenue: 0
      });
    }

    subscriptions.forEach(sub => {
      if (sub.createdAt) {
        const date = sub.createdAt?.toDate?.() || new Date(sub.createdAt);
        const monthKey = format(date, "MMM yyyy");
        const month = months.find(m => m.month === monthKey);
        const price = parseFloat(sub.price);
        if (month && !isNaN(price)) {
          month.revenue += price;
        }
      }
    });

    return months;
  };

  const getPlanDistribution = () => {
    const dist = {};
    subscriptions.forEach(sub => {
      const plan = sub.planName || "Unknown";
      dist[plan] = (dist[plan] || 0) + 1;
    });
    return Object.entries(dist).map(([name, value]) => ({ name, value }));
  };

  return (
    <div className="min-h-screen bg-green-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-green-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-2xl font-bold text-green-700">JR Home Made Foods</h1>
              <p className="text-gray-600">Admin Panel</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="p-6 max-w-7xl mx-auto">
        {/* Navigation Breadcrumb */}
        <div className="flex items-center mb-6">
          <button
            onClick={() => navigate('/admin/dashboard')}
            className="text-green-600 hover:text-green-800 mr-2"
          >
            ← Dashboard
          </button>
          <span className="text-gray-400 mx-2">/</span>
          <span className="text-gray-600">Analytics</span>
        </div>

        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-green-700">📊 Analytics Dashboard</h1>
          <div className="text-sm text-gray-600">
            {subscriptions.length} subscriptions analyzed
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto mb-3"></div>
              <p className="text-green-700">Loading analytics data...</p>
            </div>
          </div>
        ) : (
          <div className="grid gap-8 md:grid-cols-2">
            {/* Revenue Area Chart */}
            <div className="bg-white p-6 rounded-xl shadow-sm border border-green-100">
              <h2 className="text-lg font-semibold text-green-700 mb-4">📈 Monthly Revenue Trend</h2>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={getMonthlyRevenueData()}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="month" stroke="#6b7280" />
                  <YAxis tickFormatter={value => `₹${value}`} stroke="#6b7280" />
                  <Tooltip formatter={(value) => [`₹${value}`, "Revenue"]} />
                  <Area type="monotone" dataKey="revenue" stroke="#16a34a" fill="#bbf7d0" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            {/* Plan Distribution Bar Chart */}
            <div className="bg-white p-6 rounded-xl shadow-sm border border-green-100">
              <h2 className="text-lg font-semibold text-green-700 mb-4">📊 Plan Distribution</h2>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={getPlanDistribution()}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="name" stroke="#6b7280" />
                  <YAxis stroke="#6b7280" />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="value" fill="#16a34a" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Plan Breakdown Pie Chart */}
            <div className="bg-white p-6 rounded-xl shadow-sm border border-green-100">
              <h2 className="text-lg font-semibold text-green-700 mb-4">🎯 Plan Breakdown</h2>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={getPlanDistribution()}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    fill="#16a34a"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  >
                    {getPlanDistribution().map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>

            {/* Quick Actions */}
            <div className="bg-white p-6 rounded-xl shadow-sm border border-green-100">
              <h2 className="text-lg font-semibold text-green-700 mb-4">🚀 Quick Actions</h2>
              <div className="space-y-3">
                <button 
                  onClick={() => navigate('/admin/subscriptions')}
                  className="w-full text-left p-3 rounded-lg bg-green-50 hover:bg-green-100 transition-colors"
                >
                  <div className="font-medium text-green-700">View All Subscriptions</div>
                  <div className="text-sm text-green-600">Manage customer plans</div>
                </button>
                <button 
                  onClick={() => navigate('/admin/deliveries')}
                  className="w-full text-left p-3 rounded-lg bg-blue-50 hover:bg-blue-100 transition-colors"
                >
                  <div className="font-medium text-blue-700">Track Deliveries</div>
                  <div className="text-sm text-blue-600">Monitor delivery status</div>
                </button>
                <button 
                  onClick={() => navigate('/admin/customers')}
                  className="w-full text-left p-3 rounded-lg bg-purple-50 hover:bg-purple-100 transition-colors"
                >
                  <div className="font-medium text-purple-700">Customer Management</div>
                  <div className="text-sm text-purple-600">View customer profiles</div>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminAnalytics;
